import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, FormControl, Paper, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { OrderTag } from 'models/tags';
import { CheckCircle } from '@mui/icons-material';
import { CONSTANTS } from 'config/constants';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

const { TAG_COLORS } = CONSTANTS;
const FORM_ID = 'add-edit-tag-form';

type AddUpdateOrderTagDialogProps = {
    open: boolean;
    tag?: OrderTag;
    onDialogClose: () => void;
    onAdd: (tag: OrderTag) => void;
    onUpdate: (tag: OrderTag) => void;
};

const AddUpdateOrderTagDialog: FC<AddUpdateOrderTagDialogProps> = ({
    open,
    onDialogClose,
    onAdd,
    onUpdate,
    tag,
}: AddUpdateOrderTagDialogProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'construction-props', 'common']);
    const [selectedColor, setSelectedColor] = useState<string | undefined>();

    useEffect(() => {
        if (tag) {
            setSelectedColor(tag.color);
            formik.setFieldValue('color', tag.color);
        }
    }, [tag]);

    const validate = (values: OrderTag) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('lab-settings:VALIDATION:NAME');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            name: tag?.name || '',
            color: tag?.color || '',
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: (values: OrderTag) => {
            if (tag?._id) {
                onUpdate({ ...values, _id: tag?._id });
                handleDialogClose();
            } else {
                onAdd({ ...values });
                handleDialogClose();
            }
        },
    });

    const handleDialogClose = () => {
        onDialogClose();
        setSelectedColor(undefined);
        formik.resetForm();
    };

    const handleColorClick = (color: string) => () => {
        setSelectedColor(color as string);
        formik.setFieldValue('color', color);
    };

    const title = tag?._id ? translate('lab-settings:EDIT_TAG') : translate('lab-settings:ADD_TAG');

    const actions = (
        <Button form={FORM_ID} disabled={!formik.isValid || !selectedColor} variant="contained" type="submit">
            {tag?._id ? translate('common:UPDATE') : translate('common:ADD')}
        </Button>
    );

    return (
        <DHDialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth title={title} actions={actions}>
            <form id={FORM_ID} onSubmit={formik.handleSubmit}>
                <Box>
                    <FormControl margin="dense" fullWidth>
                        <TextField
                            id="name"
                            label={translate('common:NAME')}
                            required
                            helperText={formik.touched.name && formik.errors.name}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            {...formik.getFieldProps('name')}
                        />
                    </FormControl>
                </Box>
                <Typography mb={1} mt={1} variant="h4">
                    {translate('construction-props:COLOR')}*
                </Typography>
                <Box mb={1}>
                    <Paper variant="outlined">
                        <Box display="flex">
                            {TAG_COLORS.map((color, index) => (
                                <Chip
                                    key={index}
                                    label={selectedColor === color ? formik.values.name : ''}
                                    icon={
                                        selectedColor === color ? (
                                            <CheckCircle sx={{ color: '#ffffff !important' }} />
                                        ) : undefined
                                    }
                                    sx={{
                                        color: 'white',
                                        backgroundColor: color,
                                        margin: '10px',
                                        width: '100%',
                                        '&:hover': {
                                            backgroundColor: color,
                                            opacity: 0.7,
                                        },
                                    }}
                                    size="medium"
                                    onClick={handleColorClick(color)}
                                    {...formik.getFieldProps('color')}
                                />
                            ))}
                        </Box>
                    </Paper>
                </Box>
            </form>
        </DHDialog>
    );
};

export default AddUpdateOrderTagDialog;
